import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { Constants } from 'common/utils'
import { Req } from 'common/types/requests'
import { StoreStateType } from 'common/store'
import { PermissionCode } from 'common/types/enum'
import useSubscriptions from 'common/hooks/useSubscriptions'

const NON_MAIL_DESCRIPTIONS = ['WhatsApp for Business Number']

const initialState = null as number | null

export const defaultSubscriptionIdSlice = createSlice({
  initialState,
  name: 'defaultSubscriptionId',
  reducers: {
    setDefaultSubscriptionId(
      state,
      action: PayloadAction<Req['setDefaultSubscriptionId']>,
    ) {
      return action.payload
    },
  },
})

export const defaultSubscriptionIdActions = defaultSubscriptionIdSlice.actions
export const useDefaultSubscriptionActions = () => {
  const dispatch = useDispatch()
  const setDefaultSubscriptionId = useCallback(
    (payload: Req['setDefaultSubscriptionId']) => {
      return dispatch(
        defaultSubscriptionIdActions.setDefaultSubscriptionId(payload),
      )
    },
    [dispatch],
  )
  return { setDefaultSubscriptionId }
}

const selectDefaultSubscriptionId = (state: StoreStateType) =>
  state.defaultSubscriptionId

export const useDefaultSubscription = () => {
  const subscriptions = useSubscriptions()
  const { setDefaultSubscriptionId } = useDefaultSubscriptionActions()
  let defaultSubscriptionId = useSelector(selectDefaultSubscriptionId)

  if (!defaultSubscriptionId && subscriptions?.length) {
    defaultSubscriptionId =
      subscriptions.find((v) => !v.is_cancelled)?.id || subscriptions[0]?.id
  }

  if (defaultSubscriptionId && subscriptions?.length) {
    if (!subscriptions.find((v) => v.id === defaultSubscriptionId)) {
      defaultSubscriptionId = subscriptions[0]?.id
    }
  }

  const subscriptionDetail = useMemo(() => {
    return subscriptions?.find((subscription) => {
      return subscription.id === defaultSubscriptionId
    })
  }, [subscriptions, defaultSubscriptionId])

  const isAdmin =
    subscriptionDetail?.company?.role?.name === 'Administrator' ||
    subscriptionDetail?.company?.is_owner

  const hasPermission = useCallback(
    (permission: PermissionCode) => {
      const role = subscriptionDetail?.company?.role
      if (isAdmin) {
        return true
      }
      // Constants.simulate may have permissions added at runtime for testing
      const simulatePermissions = (
        Constants.simulate as { permissions?: PermissionCode[] }
      ).permissions
      const permissions = simulatePermissions || role?.permissions
      return permissions?.find((v: PermissionCode) => v === permission)
    },
    [subscriptionDetail, isAdmin],
  )

  const hasMailPlan = useMemo(() => {
    const companySubscriptions = subscriptionDetail?.company?.subscriptions
    if (!companySubscriptions?.length) return false
    return companySubscriptions.some(
      (sub) => !NON_MAIL_DESCRIPTIONS.includes(sub.description),
    )
  }, [subscriptionDetail])

  return useMemo(
    () => ({
      defaultSubscriptionId,
      hasMailPlan,
      hasPermission,
      setDefaultSubscriptionId,
      subscriptionDetail,
    }),
    [
      setDefaultSubscriptionId,
      hasMailPlan,
      hasPermission,
      defaultSubscriptionId,
      subscriptionDetail,
    ],
  )
}
